/**
 * The scaffolder tasks behind the « Tâches » page and the sidebar badge.
 *
 * TWO READS, EACH SHARED (`sharedLoad.ts`):
 *  - the page's (`'page'`): the `TASKS_LIMIT` most recent tasks, through
 *    `ScaffolderApi.listTasks`;
 *  - the badge's (`'badge'`), on every page for every user, so kept small:
 *    the `BADGE_LIMIT` most recent tasks that are running or failed —
 *    `GET /api/scaffolder/v2/tasks?status=open&status=processing&status=
 *    failed`, through `fetchApi`, since `listTasks` has no status filter.
 *    Filtered by status rather than taking the 20 most recent of all: a
 *    running task never falls out of it behind newer completed ones, and
 *    the completed tasks — most of them — are not sent at all.
 * While the page is mounted (`pagePresence.ts`) the badge makes no request:
 * it reads the page's answers (`passive`).
 *
 * SCOPE. An admin asks for every task, anyone else for their own
 * (`createdBy` = their ref). The server decides anyway: outside the admin
 * tier the permission policy answers `scaffolder.task.read` with the
 * task-owner condition, which the scaffolder applies to the list as a
 * database filter. The admin's « Mes tâches » filters the page's answer.
 *
 * WHEN IT READS (unless passive). Once on mount; then, while the document
 * is visible (`useVisiblePolling`): every 10 s while a listed task runs,
 * else every `idleIntervalMs` when given (the badge: 60 s), else not at all
 * (the page); and again when the document is shown after being hidden. A
 * shared answer is reused for `SHARED_TTL_MS`, a little under the running
 * cadence: two consumers ticking a few seconds apart share one request.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
  type IdentityApi,
} from '@backstage/frontend-plugin-api';
import {
  scaffolderApiRef,
  type ScaffolderApi,
  type ScaffolderTask,
} from '@backstage/plugin-scaffolder-react';
import { useAdminCheck, type AdminCheck } from '../home/useIsAdmin';
import {
  createSharedLoad,
  type SharedLoad,
} from '../projectEnvironments/sharedLoad';
import {
  useOnDocumentVisible,
  useVisiblePolling,
} from '../projectEnvironments/visiblePolling';
import { isRunning } from './tasks';

/** The cadence while a listed task runs. */
export const RUNNING_INTERVAL_MS = 10_000;

/** The badge's cadence while nothing runs. */
export const IDLE_BADGE_INTERVAL_MS = 60_000;

export const SHARED_TTL_MS = 9_000;

/**
 * The most recent tasks the page asks for. The scaffolder lists newest
 * first (`created_at desc` when no order is given) and the page says when
 * there are more (`totalTasks`).
 */
export const TASKS_LIMIT = 100;

/**
 * The badge's read: the most recent running or failed tasks. More running
 * tasks than this at once does not happen here; more failures since the
 * last visit would only cap the red count.
 */
export const BADGE_LIMIT = 20;
export const BADGE_STATUSES: ReadonlyArray<ScaffolderTask['status']> = [
  'open',
  'processing',
  'failed',
];

export type TaskScope = 'owned' | 'all';
export type TaskQuery = 'page' | 'badge';

export interface TaskList {
  tasks: ScaffolderTask[];
  /** Every task the read matches; more than `tasks` past its limit. */
  totalTasks?: number;
}

interface TaskApis {
  scaffolderApi: ScaffolderApi;
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
}

/** One shared load per read and scope, per app (its `ScaffolderApi`). */
const loads = new WeakMap<ScaffolderApi, Map<string, SharedLoad<TaskList>>>();

function sharedLoadOf(
  scaffolderApi: ScaffolderApi,
  query: TaskQuery,
  scope: TaskScope,
): SharedLoad<TaskList> {
  const byKey =
    loads.get(scaffolderApi) ?? new Map<string, SharedLoad<TaskList>>();
  loads.set(scaffolderApi, byKey);
  const key = `${query}:${scope}`;
  let shared = byKey.get(key);
  if (!shared) {
    shared = createSharedLoad<TaskList>(SHARED_TTL_MS);
    byKey.set(key, shared);
  }
  return shared;
}

/** The badge's request. Rejects on a failed response. */
export async function fetchBadgeTasks(
  apis: Omit<TaskApis, 'scaffolderApi'>,
  scope: TaskScope,
): Promise<TaskList> {
  const params = new URLSearchParams();
  for (const status of BADGE_STATUSES) params.append('status', status);
  if (scope === 'owned') {
    const { userEntityRef } = await apis.identityApi.getBackstageIdentity();
    params.append('createdBy', userEntityRef);
  }
  params.set('limit', String(BADGE_LIMIT));
  const baseUrl = await apis.discoveryApi.getBaseUrl('scaffolder');
  const response = await apis.fetchApi.fetch(`${baseUrl}/v2/tasks?${params}`);
  if (!response.ok) {
    throw new Error(`Scaffolder: HTTP ${response.status}`);
  }
  return (await response.json()) as TaskList;
}

/** The shared read. Rejects when the request fails. */
export function loadTasks(
  apis: TaskApis,
  query: TaskQuery,
  scope: TaskScope,
): Promise<TaskList> {
  return sharedLoadOf(apis.scaffolderApi, query, scope).load(() =>
    query === 'page'
      ? apis.scaffolderApi.listTasks({
          filterByOwnership: scope,
          limit: TASKS_LIMIT,
        })
      : fetchBadgeTasks(apis, scope),
  );
}

export type TaskListState =
  | { status: 'loading' }
  | { status: 'ready'; list: TaskList }
  | { status: 'error'; message: string; list?: TaskList };

export interface UseTaskListResult {
  admin: AdminCheck;
  state: TaskListState;
}

/** The list a state holds, if any. */
export function listOf(state: TaskListState): TaskList | undefined {
  return state.status === 'loading' ? undefined : state.list;
}

/**
 * What to ask for: nothing until the admin check answers; then every task
 * for an admin, and their own for anyone else — an unknown answer included,
 * the narrower request.
 */
function scopeOf(admin: AdminCheck): TaskScope | undefined {
  if (admin === 'pending') return undefined;
  return admin === 'admin' ? 'all' : 'owned';
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function useTaskList(options: {
  query: TaskQuery;
  /** The cadence while nothing runs; none without it. */
  idleIntervalMs?: number;
  /**
   * Makes no request: only takes the answers others fetched, starting with
   * the last one.
   */
  passive?: boolean;
}): UseTaskListResult {
  const scaffolderApi = useApi(scaffolderApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const admin = useAdminCheck();
  const scope = scopeOf(admin);
  const { query, passive = false } = options;
  const [state, setState] = useState<TaskListState>({ status: 'loading' });
  // The list on screen. A read the subscription below already announced
  // comes back to its caller too: shown once, not twice.
  const shown = useRef<TaskList>();
  const show = useCallback((list: TaskList) => {
    if (shown.current === list) return;
    shown.current = list;
    setState({ status: 'ready', list });
  }, []);

  const read = useCallback(async () => {
    if (!scope || passive) return;
    try {
      show(
        await loadTasks(
          { scaffolderApi, fetchApi, discoveryApi, identityApi },
          query,
          scope,
        ),
      );
    } catch (error) {
      shown.current = undefined;
      setState(previous => ({
        status: 'error',
        message: messageOf(error),
        list: listOf(previous),
      }));
    }
  }, [
    scaffolderApi,
    fetchApi,
    discoveryApi,
    identityApi,
    query,
    scope,
    passive,
    show,
  ]);

  // The answers fetched by whoever asked — and, passive, the last one.
  useEffect(() => {
    if (!scope) return undefined;
    const shared = sharedLoadOf(scaffolderApi, query, scope);
    const latest = shared.latest();
    if (passive && latest) show(latest);
    return shared.subscribe(show);
  }, [scaffolderApi, query, scope, passive, show]);

  useEffect(() => {
    // `read` turns its own failure into state: nothing can reject.
    void read();
  }, [read]);

  const running = listOf(state)?.tasks.some(isRunning) ?? false;
  const intervalMs = running ? RUNNING_INTERVAL_MS : options.idleIntervalMs;
  useVisiblePolling(
    read,
    intervalMs ?? RUNNING_INTERVAL_MS,
    scope !== undefined && !passive && intervalMs !== undefined,
  );
  useOnDocumentVisible(() => {
    void read();
  });

  return { admin, state };
}
